/**
 * FLAVOR FREQUENCY POPUP
 * Uses the same variant classification globals as the main product page:
 *   window.quarterlyVariantIds, window.isOneTimePurchaseVariant, window.formatMoney
 * Replicates the exact 90-day card / 30-day card / one-time link layout.
 */
(function () {
  'use strict';

  var _backdrop = null;
  var _productCache = {};
  var _options = [];       // subscription cards (90-day, 30-day)
  var _oneTimeOption = null; // rendered as plain link
  var _selectedIndex = 0;
  var _isAdding = false;
  var _currentHandle = '';
  var _currentFlavor = '';
  var _currentVariantIds = null; // { quarterly, monthly, onetime } variant IDs from Liquid

  // ── Mount ──────────────────────────────────────────────────────────────────

  function mount() {
    var existing = document.getElementById('ffp-backdrop');
    if (existing) {
      // Reuse existing DOM element but re-bind all listeners to THIS closure's functions.
      // This handles the case where the script IIFE ran twice (theme editor re-render, etc.)
      // and the old listeners point to a stale closure with empty _options/_backdrop state.
      _backdrop = existing;
      _bindListeners();
      return;
    }

    var el = document.createElement('div');
    el.id = 'ffp-backdrop';
    el.className = 'ffp-backdrop';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Select purchase frequency');
    el.innerHTML = [
      '<div class="ffp-card">',
        /* Close button lives on the card, above all state layers */
        '<button class="ffp-close" id="ffp-close" aria-label="Close">',
          '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">',
            '<path d="M1 1l12 12M13 1L1 13"/>',
          '</svg>',
        '</button>',

        /* All three states live inside .ffp-states at all times.
           Only opacity + pointer-events change — zero layout cost, butter smooth. */
        '<div class="ffp-states" id="ffp-states">',

          '<div id="ffp-state-loading" class="ffp-loading">',
            '<div class="ffp-loading__bar"></div>',
            '<div class="ffp-loading__skeleton">',
              '<div class="ffp-sk-header">',
                '<div class="ffp-sk ffp-sk--eyebrow"></div>',
                '<div class="ffp-sk ffp-sk--title"></div>',
                '<div class="ffp-sk ffp-sk--subtitle"></div>',
              '</div>',
              '<div class="ffp-sk ffp-sk--divider"></div>',
              '<div class="ffp-sk-option ffp-sk-option--featured">',
                '<div class="ffp-sk-option__left">',
                  '<div class="ffp-sk ffp-sk--radio"></div>',
                  '<div class="ffp-sk-option__text">',
                    '<div class="ffp-sk ffp-sk--opt-title"></div>',
                    '<div class="ffp-sk ffp-sk--opt-sub"></div>',
                  '</div>',
                '</div>',
                '<div class="ffp-sk-option__right">',
                  '<div class="ffp-sk ffp-sk--price"></div>',
                  '<div class="ffp-sk ffp-sk--serving"></div>',
                '</div>',
              '</div>',
              '<div class="ffp-sk-option">',
                '<div class="ffp-sk-option__left">',
                  '<div class="ffp-sk ffp-sk--radio"></div>',
                  '<div class="ffp-sk-option__text">',
                    '<div class="ffp-sk ffp-sk--opt-title"></div>',
                    '<div class="ffp-sk ffp-sk--opt-sub"></div>',
                  '</div>',
                '</div>',
                '<div class="ffp-sk-option__right">',
                  '<div class="ffp-sk ffp-sk--price"></div>',
                  '<div class="ffp-sk ffp-sk--serving"></div>',
                '</div>',
              '</div>',
              '<div class="ffp-sk ffp-sk--cta"></div>',
            '</div>',
          '</div>',

          '<div id="ffp-state-error" class="ffp-error">',
            '<p class="ffp-error__text" id="ffp-error-msg"></p>',
            '<button class="ffp-error__retry" id="ffp-retry">Try Again</button>',
          '</div>',

          '<div id="ffp-state-main">',
            '<header class="ffp-header">',
              '<span class="ffp-eyebrow">Select Frequency</span>',
              '<h2 class="ffp-flavor-name" id="ffp-flavor-name"></h2>',
              '<p class="ffp-subline">Daily Ultimate Essentials PRO</p>',
            '</header>',
            '<div class="ffp-divider"></div>',
            '<div class="ffp-body">',
              '<div class="ffp-options" id="ffp-options" role="radiogroup"></div>',
              '<div id="ffp-onetime-wrap" class="ffp-onetime-wrap" style="display:none">',
                '<button class="ffp-onetime-link" id="ffp-onetime-btn"></button>',
              '</div>',
              '<button class="ffp-cta" id="ffp-atc">',
                '<span class="ffp-cta__spinner"></span>',
                '<span class="ffp-cta__label" id="ffp-atc-label">Add to Cart</span>',
              '</button>',
              '<p class="ffp-footer-note">Cancel or pause anytime &nbsp;&middot;&nbsp; Free shipping for all subscription orders</p>',
            '</div>',
          '</div>',

        '</div>',
      '</div>'
    ].join('');

    document.body.appendChild(el);
    _backdrop = el;
    _bindListeners();
  }

  // Attach (or re-attach) all button listeners to the current closure's functions.
  // Stores the current handler on the element so it can be removed before re-adding,
  // preventing duplicate firings when the script IIFE runs more than once.
  function _bindListeners() {
    function rebind(id, handler) {
      var el = document.getElementById(id);
      if (!el) return;
      if (el._ffpHandler) el.removeEventListener('click', el._ffpHandler);
      el._ffpHandler = handler;
      el.addEventListener('click', handler);
    }
    rebind('ffp-close', close);
    rebind('ffp-retry', retry);
    rebind('ffp-atc', addToCart);
    rebind('ffp-onetime-btn', addOneTimeToCart);

    // Backdrop click-outside and Escape key: only bind once per DOM element.
    if (!_backdrop.dataset.ffpBound) {
      _backdrop.dataset.ffpBound = '1';
      _backdrop.addEventListener('click', function (e) {
        if (e.target === _backdrop) close();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && _backdrop && _backdrop.classList.contains('is-open')) close();
      });
    }
  }

  // ── Public open ────────────────────────────────────────────────────────────

  function hexToRgb(hex) {
    var clean = (hex || '').replace('#', '');
    if (clean.length === 3) clean = clean[0]+clean[0]+clean[1]+clean[1]+clean[2]+clean[2];
    var n = parseInt(clean, 16);
    if (isNaN(n)) return null;
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }

  function applyFlavorColors(colors) {
    var card = _backdrop ? _backdrop.querySelector('.ffp-card') : null;
    if (!card) return;
    var primary = hexToRgb(colors.primary);
    var glow    = hexToRgb(colors.glow);
    if (primary) {
      card.style.setProperty('--ffp-color-r', primary.r);
      card.style.setProperty('--ffp-color-g', primary.g);
      card.style.setProperty('--ffp-color-b', primary.b);
    }
    if (glow) {
      card.style.setProperty('--ffp-glow-r', glow.r);
      card.style.setProperty('--ffp-glow-g', glow.g);
      card.style.setProperty('--ffp-glow-b', glow.b);
    }
  }

  function open(config) {
    _currentHandle = config.handle || '';
    _currentFlavor = config.flavor || '';
    // Treat {quarterly:null, monthly:null, onetime:null} same as no IDs provided
    var vids = config.variantIds || {};
    _currentVariantIds = (vids.quarterly || vids.monthly || vids.onetime) ? vids : null;
    _selectedIndex = 0;

    if (!_backdrop) mount();

    var card = _backdrop.querySelector('.ffp-card');

    // Clear any in-progress close
    if (card) card.classList.remove('is-closing', 'is-bloom');
    _backdrop.classList.remove('is-closing-backdrop');

    applyFlavorColors(config.colors || {});

    // If product is already cached, skip the loading state entirely
    var cached = _productCache[_currentHandle];
    if (cached) {
      var built = buildOptions(cached, _currentFlavor);
      _options = built.subscriptions;
      _oneTimeOption = built.onetime;
      renderOptions();
      // Pre-set main state as visible before opening so it unveils with the card
      _showStateSilent('main');
      _backdropOpen(card);
    } else {
      // Show loading, open, then fetch
      _showStateSilent('loading');
      _backdropOpen(card);
      fetchProduct(_currentHandle)
        .then(function(product) {
          var built = buildOptions(product, _currentFlavor);
          _options = built.subscriptions;
          _oneTimeOption = built.onetime;
          if (!_options.length && !_oneTimeOption) {
            showError('No purchase options found.');
            return;
          }
          renderOptions();
          showState('main');
        })
        .catch(function() {
          showError('Something went wrong. Please try again.');
        });
    }
  }

  // Open the backdrop + trigger bloom — shared by both paths
  function _backdropOpen(card) {
    _backdrop.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    // Light bloom sweep fires 100ms after card starts entering
    if (card) {
      setTimeout(function() {
        card.classList.add('is-bloom');
        setTimeout(function() { card.classList.remove('is-bloom'); }, 900);
      }, 100);
    }
  }

  // Set a state instantly (no transition) — used before the backdrop opens
  // so the correct content is already in place when the card animates in.
  // Children of #ffp-state-main are reset to their pre-stagger state so they
  // animate in beautifully alongside the card entry.
  var STATE_IDS = { loading: 'ffp-state-loading', error: 'ffp-state-error', main: 'ffp-state-main' };

  // Switch to a state. The outgoing state gets display:none (via removing is-active),
  // the incoming state gets display:block + plays its entry animation.
  // The card naturally resizes to fit — no height measurement needed.
  function _switchState(state, silent) {
    Object.keys(STATE_IDS).forEach(function(key) {
      var el = document.getElementById(STATE_IDS[key]);
      if (!el) return;
      if (key === state) {
        if (key === 'main' && !silent) {
          // Reset child stagger so children animate in fresh
          var children = el.querySelectorAll('.ffp-header,.ffp-divider,.ffp-options,.ffp-onetime-wrap,.ffp-cta,.ffp-footer-note');
          children.forEach(function(c) {
            c.style.animation = 'none';
            c.style.opacity   = '';
            c.style.transform = '';
          });
          void el.offsetHeight;
          children.forEach(function(c) { c.style.animation = ''; });
        }
        // Force animation replay by removing and re-adding the class
        el.classList.remove('is-active');
        void el.offsetHeight; // flush so animation restarts
        el.classList.add('is-active');
      } else {
        el.classList.remove('is-active');
      }
    });
  }

  function _showStateSilent(state) { _switchState(state, true); }
  function showState(state)        { _switchState(state, false); }

  function close() {
    if (!_backdrop) return;
    var card = _backdrop.querySelector('.ffp-card');

    document.body.style.overflow = '';
    _backdrop.style.pointerEvents = 'none';

    // Animate card out
    if (card) card.classList.add('is-closing');
    // Fade backdrop veil simultaneously
    _backdrop.classList.add('is-closing-backdrop');

    // After animation completes, reset everything for next open
    setTimeout(function() {
      _backdrop.classList.remove('is-open', 'is-closing-backdrop');
      _backdrop.style.pointerEvents = '';
      if (card) card.classList.remove('is-closing');

      // Clear all active states — card collapses to zero, ready for next open
      Object.keys(STATE_IDS).forEach(function(key) {
        var el = document.getElementById(STATE_IDS[key]);
        if (el) el.classList.remove('is-active');
      });
    }, 420);
  }

  function retry() {
    open({ handle: _currentHandle, flavor: _currentFlavor });
  }

  // ── States ─────────────────────────────────────────────────────────────────

  function showError(msg) {
    document.getElementById('ffp-error-msg').textContent = msg;
    showState('error');
  }

  // ── Option building ────────────────────────────────────────────────────────
  // Mirrors the logic in buy-buttons.liquid / product-variant-options.liquid:
  //   quarterly  = window.quarterlyVariantIds includes variant.id
  //   one-time   = any option value contains "one time" (case-insensitive)
  //   30-day     = everything else (not quarterly, not one-time, not double-sub, not hidden)

  function buildOptions(product, flavor) {
    var variants = product.variants || [];
    var sellingPlanGroups = product.selling_plan_groups || [];

    // Build selling plan map: id -> plan object
    var planMap = {};
    sellingPlanGroups.forEach(function (g) {
      (g.selling_plans || []).forEach(function (p) { planMap[p.id] = p; });
    });

    var quarterly = null;
    var monthly   = null;
    var onetime   = null;

    // ── Fast path: use Liquid-embedded variant IDs (always correct, no guessing) ──
    var ids = _currentVariantIds;
    if (ids && (ids.quarterly || ids.monthly || ids.onetime)) {
      // findById declared at statement level — safe in strict mode on all browsers
      var findById = function(id) {
        if (!id) return null;
        var n = parseInt(id);
        for (var i = 0; i < variants.length; i++) {
          if (parseInt(variants[i].id) === n) return variants[i];
        }
        return null;
      };
      quarterly = findById(ids.quarterly);
      monthly   = findById(ids.monthly);
      onetime   = findById(ids.onetime);
    } else {
      // ── Fallback: fuzzy flavor matching + window globals ──
      var quarterlyIds = window.quarterlyVariantIds || [];
      var doubleSubIds = window.doubleSubscriptionVariantIds || [];
      var hiddenIds    = window.hiddenVariantIds || [];

      var norm = function(s) {
        return (s || '').toLowerCase()
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9 ]/g, ' ')
          .replace(/\s+/g, ' ').trim();
      };

      var flavorNorm = norm(flavor);
      var flavorVariants = variants.filter(function (v) { return norm(v.option1) === flavorNorm; });
      if (!flavorVariants.length) {
        var flavorWords = flavorNorm.split(' ').filter(function(w) { return w.length > 2; });
        flavorVariants = variants.filter(function (v) {
          var vNorm = norm(v.option1);
          return flavorWords.some(function(w) { return vNorm.indexOf(w) !== -1; });
        });
      }
      if (!flavorVariants.length) flavorVariants = variants;

      flavorVariants.forEach(function (v) {
        var vid = parseInt(v.id);
        if (hiddenIds.indexOf(vid) !== -1) return;
        var isOnetime   = isOneTime(v);
        var isQuarterly = quarterlyIds.indexOf(vid) !== -1;
        var isDoubleSub = doubleSubIds.indexOf(vid) !== -1;
        if (isOnetime)        { if (!onetime)   onetime   = v; }
        else if (isQuarterly) { if (!quarterly) quarterly = v; }
        else if (!isDoubleSub){ if (!monthly)   monthly   = v; }
      });
    }

    var subscriptions = [];
    if (quarterly) subscriptions.push(makeOption('quarterly', quarterly, planMap, true));
    if (monthly)   subscriptions.push(makeOption('monthly',   monthly,   planMap, false));

    return {
      subscriptions: subscriptions,
      onetime: onetime ? makeOption('onetime', onetime, null, false) : null,
      _dbg: {
        quarterly: quarterly ? quarterly.id + ' o1="' + quarterly.option1 + '"' : 'null',
        monthly:   monthly   ? monthly.id   + ' o1="' + monthly.option1   + '"' : 'null',
        onetime:   onetime   ? onetime.id   + ' o1="' + onetime.option1   + '"' : 'null',
      }
    };
  }

  function isOneTime(variant) {
    // Translation-safe: one-time variants have no selling plan allocations.
    // Falls back to English text check for edge cases.
    if (variant.selling_plan_allocations && variant.selling_plan_allocations.length === 0) return true;
    var opts = [variant.option1, variant.option2, variant.option3, variant.option4].filter(Boolean);
    return opts.some(function (o) { return o.toLowerCase().indexOf('one time') !== -1; });
  }

  function makeOption(id, variant, planMap, isBestValue) {
    var planId = null;
    var allocations = variant.selling_plan_allocations || [];
    if (allocations.length && id !== 'onetime') {
      planId = allocations[0].selling_plan_id;
    }

    var label = id === 'quarterly' ? '90-Day Supply' : id === 'monthly' ? '30-Day Supply' : 'One-Time Purchase';

    // Pull metafield data embedded by Liquid (keyed by variant ID as number or string)
    var meta = (window.ffpVariantData && (window.ffpVariantData[variant.id] || window.ffpVariantData[String(variant.id)])) || {};

    // Savings text: prefer metafield, fallback to computed %
    var savings = meta.savingsText || null;
    if (!savings && variant.compare_at_price && variant.compare_at_price > variant.price) {
      savings = 'Save ' + Math.round((1 - variant.price / variant.compare_at_price) * 100) + '%';
    }

    // /mo display price: quarterly = price/3, others = price as-is
    var displayPrice = variant.price;
    if (id === 'quarterly' && meta.quarterlyPriceCents) {
      displayPrice = meta.quarterlyPriceCents;
    }

    // Pre-formatted prices from Liquid (correct currency for all markets)
    var fmtPrice = (id === 'quarterly' && meta.formattedMonthlyPrice) ? meta.formattedMonthlyPrice : meta.formattedPrice;
    var fmtCompare = meta.formattedComparePrice || null;

    return {
      id: id,
      label: label,
      savings: savings,
      isBestValue: isBestValue,
      price: displayPrice,
      fullPrice: variant.price,
      comparePrice: variant.compare_at_price || null,
      formattedPrice: fmtPrice || null,
      formattedComparePrice: fmtCompare,
      formattedFullPrice: meta.formattedPrice || null,
      variantId: variant.id,
      sellingPlanId: planId,
      billingFrequency: meta.billingFrequency || null,
      pricePerServing: meta.pricePerServing || null,
      description: meta.description || null,
      welcomeKit: meta.welcomeKit || null,
      isQuarterly: id === 'quarterly'
    };
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  function renderOptions() {
    document.getElementById('ffp-flavor-name').textContent = _currentFlavor;
    var container = document.getElementById('ffp-options');
    container.innerHTML = '';

    _options.forEach(function (opt, idx) {
      var div = document.createElement('div');
      div.className = 'ffp-option' + (idx === _selectedIndex ? ' is-selected' : '');
      div.setAttribute('role', 'radio');
      div.setAttribute('aria-checked', idx === _selectedIndex ? 'true' : 'false');
      div.setAttribute('tabindex', idx === _selectedIndex ? '0' : '-1');
      div.dataset.index = idx;

      var savingsHtml = opt.savings
        ? ' <span class="ffp-option__savings">(' + opt.savings + ')</span>'
        : '';

      // Detect if a string starts with any emoji or non-ASCII symbol
      // Covers: Emoji_Presentation, text-presentation emoji (🏋 etc), variation selectors
      function startsWithEmoji(str) {
        var s = str || '';
        if (!s.length) return false;
        var cp = s.codePointAt(0);
        // Non-ASCII (> 127) and not a plain letter/digit = treat as emoji/symbol
        return cp > 127;
      }

      // Price display — main + strikethrough on same row, serving below
      // Prefer Liquid-formatted prices (correct currency symbol for all markets)
      var mainPriceStr = opt.formattedPrice || fmt(opt.price);
      var comparePriceStr = opt.formattedComparePrice || fmt(opt.comparePrice);
      var priceRowHtml = '<span class="ffp-option__price-main">' + mainPriceStr + '<span class="ffp-option__price-suffix">/mo</span></span>';
      if (opt.comparePrice && opt.comparePrice > opt.price) {
        priceRowHtml += '<span class="ffp-option__price-compare">' + comparePriceStr + '</span>';
      }
      var priceHtml = '<div class="ffp-option__price-row">' + priceRowHtml + '</div>';
      if (opt.pricePerServing) {
        priceHtml += '<span class="ffp-option__price-serving">' + opt.pricePerServing + ' / serving</span>';
      }

      // Billing frequency line
      var billingHtml = opt.billingFrequency
        ? '<p class="ffp-option__billing">' + opt.billingFrequency + '</p>'
        : '';

      // Bullet points
      // - Quarterly (90-day): emoji items shown as-is (emoji IS the bullet, no tick)
      // - Monthly (30-day): plain-text items get sub_check tick
      // Only filter: Free Welcome Kit (shown separately)
      var bulletsHtml = '';
      if (opt.description && opt.description.length) {
        var items = opt.description.filter(function(d) {
          return d && d.indexOf('Free Welcome Kit') === -1;
        });
        if (opt.welcomeKit) items.push(opt.welcomeKit);
        if (items.length) {
          bulletsHtml = '<ul class="ffp-option__bullets">' +
            items.map(function(d) {
              // Emoji items: no extra icon. Plain-text items: sub_check via .no-emoji class
              var cls = startsWithEmoji(d) ? '' : ' class="no-emoji"';
              return '<li' + cls + '>' + d + '</li>';
            }).join('') +
          '</ul>';
        }
      }

      div.innerHTML = [
        opt.isBestValue ? '<span class="ffp-option__badge ffp-option__badge--popular">Best Value</span>' : '',
        '<div class="ffp-option__inner">',
          '<span class="ffp-option__checkbox" aria-hidden="true"></span>',
          '<div class="ffp-option__text">',
            '<p class="ffp-option__name">' + opt.label + savingsHtml + '</p>',
            billingHtml,
          '</div>',
          '<div class="ffp-option__price">' + priceHtml + '</div>',
        '</div>',
        bulletsHtml
      ].join('');

      div.addEventListener('click', function () { selectOption(idx); });
      div.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectOption(idx); }
        if (e.key === 'ArrowDown') { e.preventDefault(); selectOption(Math.min(idx + 1, _options.length - 1)); }
        if (e.key === 'ArrowUp')   { e.preventDefault(); selectOption(Math.max(idx - 1, 0)); }
      });
      container.appendChild(div);
    });

    // One-time link
    var otwrap = document.getElementById('ffp-onetime-wrap');
    var otbtn  = document.getElementById('ffp-onetime-btn');
    if (_oneTimeOption) {
      otbtn.textContent = 'One Time Purchase ' + (_oneTimeOption.formattedFullPrice || _oneTimeOption.formattedPrice || fmt(_oneTimeOption.price));
      otwrap.style.display = '';
    } else {
      otwrap.style.display = 'none';
    }

    updateAtcLabel();
  }

  function selectOption(idx) {
    _selectedIndex = idx;
    var items = document.querySelectorAll('#ffp-options .ffp-option');
    items.forEach(function (el, i) {
      var sel = i === idx;
      el.classList.toggle('is-selected', sel);
      el.setAttribute('aria-checked', sel ? 'true' : 'false');
      el.setAttribute('tabindex', sel ? '0' : '-1');
    });
    if (items[idx]) items[idx].focus();
    updateAtcLabel();
  }

  function updateAtcLabel() {
    var opt = _options[_selectedIndex];
    if (!opt) return;
    var priceStr = (opt.formattedPrice || fmt(opt.price)) + '/mo';
    var label = opt.isQuarterly
      ? 'Start My 90-Day Program \u2014 ' + priceStr
      : 'Add to Cart \u2014 ' + priceStr;
    var el = document.getElementById('ffp-atc-label');
    if (!el) return;
    // Skip animation if text hasn't changed
    if (el.textContent === label) return;
    el.classList.add('is-switching');
    setTimeout(function() {
      el.textContent = label;
      el.classList.remove('is-switching');
    }, 180);
  }

  // ── Cart ───────────────────────────────────────────────────────────────────

  function addToCart() {
    if (_isAdding) return;
    var opt = _options[_selectedIndex];
    if (!opt) return;
    doAddToCart(opt.variantId, opt.sellingPlanId);
  }

  function addOneTimeToCart() {
    if (_isAdding || !_oneTimeOption) return;
    doAddToCart(_oneTimeOption.variantId, null);
  }

  function doAddToCart(variantId, sellingPlanId) {
    _isAdding = true;
    var btn = document.getElementById('ffp-atc');
    btn.classList.add('is-loading');
    btn.disabled = true;

    var cartDrawer = document.querySelector('cart-drawer');
    var cartNotification = document.querySelector('cart-notification');

    // IM8Cart (global.js): Standard Actions first, guarded Ajax second.
    // Resolves to the /cart/add.js contract so the handlers below are unchanged.
    var mainItem = { id: Number(variantId), quantity: 1 };
    if (sellingPlanId) mainItem.selling_plan = Number(sellingPlanId);
    var sectionIds = [];
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      sectionIds = cartDrawer.getSectionsToRender().map(function(s) { return s.id; });
    }

    window.IM8Cart.add([mainItem], { sections: sectionIds, context: 'product' })
    .then(function (data) {
      // Shopify returns { status: 422, description: '...' } on error
      if (data && data.status) {
        var msg = data.description || data.message || 'Could not add to cart';
        console.error('[FFP] cart add error:', msg, data);
        throw new Error(msg);
      }

      // Trigger unified cart listeners (including welcome gift sync) for this add flow.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        var addEventType = (typeof EVENT_TYPE !== 'undefined' && EVENT_TYPE.add) ? EVENT_TYPE.add : 'add';
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'flavor-frequency-popup',
          cartData: data,
          eventType: addEventType
        });
      }

      btn.classList.remove('is-loading');
      btn.classList.add('is-success');
      document.getElementById('ffp-atc-label').textContent = 'Added \u2713';

      if (cartDrawer && data.sections && Object.keys(data.sections).length) {
        // Sections returned inline — render directly
        cartDrawer.renderContents(data);
        if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
      } else if (cartDrawer) {
        // 2026-04-28: in Rebuy mode, the shim only consumes cart-icon-bubble —
        // fetching the cart-drawer section is wasted bytes + parse work that
        // contributes to the post-open hiccup. Skip cart-drawer in that mode,
        // refresh only the bubble for an instant count update, and let the
        // shim's renderContents → openPreferredCart → Rebuy.show pipeline take
        // over the drawer.
        // Use Jonah's unified gate: shim active AND Rebuy healthy. Matches
        // beckham-stack-popup.js so all popup ATC paths skip cart-drawer
        // identically. Falls through to native fetch when Rebuy is loading
        // or failed.
        var skipNative = (typeof window.IM8RebuySmartCartShouldSkipNativeDrawerSections === 'function') &&
                         window.IM8RebuySmartCartShouldSkipNativeDrawerSections();
        var cartUrl = (window.routes && window.routes.cart_url) ? window.routes.cart_url : '/cart';
        if (skipNative) {
          fetch(cartUrl + '?section_id=cart-icon-bubble', { credentials: 'same-origin' })
            .then(function (r) { return r.text(); })
            .then(function (bubbleHtml) {
              cartDrawer.renderContents({ sections: { 'cart-icon-bubble': bubbleHtml }, item_count: 1 });
              if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
            })
            .catch(function (e) {
              console.error('[FFP] bubble refresh failed', e);
              // Last resort: ask shim to open Rebuy directly; bubble may lag
              // until next rebuy:cart.change event but the drawer still opens.
              document.dispatchEvent(new CustomEvent('im8:rebuy-cart-open-requested'));
            });
        } else {
          // Native drawer mode — fetch both sections then render
          Promise.all([
            fetch(cartUrl + '?section_id=cart-drawer', { credentials: 'same-origin' }),
            fetch(cartUrl + '?section_id=cart-icon-bubble', { credentials: 'same-origin' })
          ]).then(function(responses) {
            return Promise.all(responses.map(function(r) { return r.text(); }));
          }).then(function(htmls) {
            cartDrawer.renderContents({ sections: { 'cart-drawer': htmls[0], 'cart-icon-bubble': htmls[1] }, item_count: 1 });
            if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
          }).catch(function(e) {
            console.error('[FFP] drawer refresh failed', e);
            if (typeof cartDrawer.open === 'function') cartDrawer.open();
          });
        }
      } else if (cartNotification && typeof cartNotification.renderContents === 'function') {
        cartNotification.renderContents(data);
      }

      setTimeout(function () {
        close();
        btn.classList.remove('is-success');
        btn.disabled = false;
        _isAdding = false;
      }, 1200);
    })
    .catch(function (err) {
      console.error('[FFP] cart error', err);
      if (window.IM8CartGuard && err && (err.cartBlocked || err.name === 'AbortError')) {
        // Bot-protection challenge / hung edge: hand the add to a GET
        // permalink so the user clears the challenge once.
        window.IM8CartGuard.recoverViaNavigation(mainItem, { returnTo: '/cart' });
        return;
      }
      btn.classList.remove('is-loading');
      btn.disabled = false;
      _isAdding = false;
      document.getElementById('ffp-atc-label').textContent = 'Error \u2014 Try Again';
      setTimeout(updateAtcLabel, 2000);
    });
  }

  // ── Fetch ──────────────────────────────────────────────────────────────────

  function fetchProduct(handle) {
    if (_productCache[handle]) return Promise.resolve(_productCache[handle]);
    // Use credentials:'include' so the storefront password cookie is sent on
    // password-protected / development stores. Try .js first, fall back to .json.
    var url = '/products/' + handle + '.js';
    return fetch(url, { credentials: 'include', headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) {
          // Fallback: try .json endpoint (works on some dev store configurations)
          return fetch('/products/' + handle + '.json', { credentials: 'include', headers: { Accept: 'application/json' } })
            .then(function (res2) {
              if (!res2.ok) throw new Error('Product not found: ' + handle + ' (tried .js and .json)');
              return res2.json().then(function(d) { return d.product || d; });
            });
        }
        return res.json();
      })
      .then(function (data) {
        _productCache[handle] = data;
        return data;
      });
  }

  // ── Utils ──────────────────────────────────────────────────────────────────

  function fmt(cents) {
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(cents, false);
    }
    if (!cents && cents !== 0) return '';
    var sym = (window.shopCurrency && window.shopCurrency.symbol) ? window.shopCurrency.symbol : '$';
    var d = cents / 100;
    return sym + (d % 1 === 0 ? d.toFixed(0) : d.toFixed(2));
  }

  // ── Expose ─────────────────────────────────────────────────────────────────

  window.FlavorFrequencyPopup = { open: open, close: close, mount: mount };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }

})();
